// web/src/types/schedule.ts - Schedule & comparison types

import type { Project } from './project';
import type { Scan, ScanConfig } from './scan';

export interface ScanSchedule {
  id: number;
  project_id: number;
  created_by: number;
  name: string;
  description: string;
  config: ScanConfig;
  enabled: boolean;
  cron_expr: string;
  next_run?: string;
  last_run?: string;
  last_run_scan_id?: number;
  target_ids: string; // Comma-separated target IDs
  target_selection_mode: 'fixed' | 'all_project' | 'dynamic';
  run_count: number;
  max_runs: number;
  success_count: number;
  failure_count: number;
  enable_comparison: boolean;
  compare_with_last_n: number;
  retain_historical_scans: number;
  notify_on_completion: boolean;
  notify_on_failure: boolean;
  notify_on_new_vulns: boolean;
  notification_emails: string;
  notification_recipient_ids?: number[]; // IDs of contacts to notify
  // Scheduled Report Generation
  auto_generate_report?: boolean;
  report_format?: 'pdf' | 'html' | 'csv' | 'json';
  report_language?: 'en' | 'de' | 'fr' | 'it';
  report_include_summary?: boolean;
  report_include_diff?: boolean;
  email_report_to_owner?: boolean;
  email_report_to_emails?: string;
  // Agent for remote execution
  agent_id?: string;
  // Nuclei engine override per-schedule
  nuclei_engine_override?: '' | 'custom' | 'native' | 'hybrid' | 'auto-rotate';
  last_nuclei_engine?: string;
  created_at: string;
  updated_at: string;
  // Relations
  project?: Project;
  last_scan?: Scan;
  executions?: ScheduleExecution[];
}

export interface ScheduleExecution {
  id: number;
  schedule_id: number;
  scan_id: number;
  execution_number: number;
  scheduled_time: string;
  started_at?: string;
  completed_at?: string;
  duration: number; // Duration in seconds
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  error_message?: string;
  triggered_by: 'scheduler' | 'manual' | 'api';
  config_snapshot?: string;
  target_snapshot?: string;
  total_targets: number;
  total_results: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  info_count: number;
  new_vulns_count: number;
  resolved_vulns_count: number;
  unchanged_vulns_count: number;
  delta_summary?: string;
  created_at: string;
  updated_at: string;
  // Relations
  schedule?: ScanSchedule;
  scan?: Scan;
}

export interface VulnDelta {
  result_id: number;
  host: string;
  port: number;
  severity: string;
  title: string;
  vuln_type: string;
  status: 'new' | 'resolved' | 'persistent';
  fingerprint: string;
}

export interface DeltaSummary {
  new_vulns: VulnDelta[];
  resolved_vulns: VulnDelta[];
  persistent_vulns: VulnDelta[];
  new_count: number;
  resolved_count: number;
  persistent_count: number;
}

export interface ScheduleComparison {
  schedule_id: number;
  schedule_name: string;
  execution_1: ScheduleExecution;
  execution_2: ScheduleExecution;
  delta: DeltaSummary;
  severity_delta: {
    critical: number;
    high: number;
    medium: number;
    low: number;
    info: number;
  };
  comparison_time: string;
}

export interface ScheduleTrend {
  schedule_id: number;
  schedule_name: string;
  executions: ScheduleExecution[];
  trend_data: TrendDataPoint[];
  overall_trend: 'improving' | 'stable' | 'worsening';
  summary: {
    total_executions: number;
    avg_duration: number;
    success_rate: number;
    avg_new_vulns: number;
    avg_resolved_vulns: number;
  };
}

export interface TrendDataPoint {
  execution_number: number;
  execution_id: number;
  date: string;
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
  total: number;
  new_vulns: number;
  resolved_vulns: number;
}

export interface SchedulerStatus {
  running: boolean;
  check_interval: string;
  max_concurrent: number;
  active_executions: number;
  upcoming_schedules: UpcomingSchedule[];
  last_check: string;
}

export interface UpcomingSchedule {
  schedule_id: number;
  schedule_name: string;
  project_id: number;
  next_run: string;
  cron_expr: string;
  description: string;
}

export interface CronPreset {
  key: string;
  label: string;
  cron_expr: string;
  description: string;
}

// Schedule form types
export interface CreateScheduleRequest {
  name: string;
  description?: string;
  config: ScanConfig;
  enabled?: boolean;
  cron_expr: string;
  target_ids?: number[];
  target_selection_mode?: 'fixed' | 'all_project' | 'dynamic';
  max_runs?: number;
  enable_comparison?: boolean;
  compare_with_last_n?: number;
  retain_historical_scans?: number;
  notify_on_completion?: boolean;
  notify_on_failure?: boolean;
  notify_on_new_vulns?: boolean;
  notification_emails?: string;
}

export interface UpdateScheduleRequest {
  name?: string;
  description?: string;
  config?: ScanConfig;
  enabled?: boolean;
  cron_expr?: string;
  target_ids?: number[];
  target_selection_mode?: 'fixed' | 'all_project' | 'dynamic';
  max_runs?: number;
  enable_comparison?: boolean;
  compare_with_last_n?: number;
  retain_historical_scans?: number;
  notify_on_completion?: boolean;
  notify_on_failure?: boolean;
  notify_on_new_vulns?: boolean;
  notification_emails?: string;
}

// Schedule constants
export const SCHEDULE_STATUSES = ['pending', 'running', 'completed', 'failed', 'cancelled'] as const;
export const TARGET_SELECTION_MODES = ['fixed', 'all_project', 'dynamic'] as const;
export const TRIGGERED_BY_OPTIONS = ['scheduler', 'manual', 'api'] as const;

// Common cron presets
export const CRON_PRESETS: CronPreset[] = [
  { key: 'every_minute', label: 'Every Minute', cron_expr: '* * * * *', description: 'Run every minute (testing only)' },
  { key: 'every_5_minutes', label: 'Every 5 Minutes', cron_expr: '*/5 * * * *', description: 'Run every 5 minutes' },
  { key: 'every_15_minutes', label: 'Every 15 Minutes', cron_expr: '*/15 * * * *', description: 'Run every 15 minutes' },
  { key: 'every_30_minutes', label: 'Every 30 Minutes', cron_expr: '*/30 * * * *', description: 'Run every 30 minutes' },
  { key: 'hourly', label: 'Hourly', cron_expr: '0 * * * *', description: 'Run at the start of every hour' },
  { key: 'every_2_hours', label: 'Every 2 Hours', cron_expr: '0 */2 * * *', description: 'Run every 2 hours' },
  { key: 'every_6_hours', label: 'Every 6 Hours', cron_expr: '0 */6 * * *', description: 'Run every 6 hours' },
  { key: 'every_12_hours', label: 'Every 12 Hours', cron_expr: '0 */12 * * *', description: 'Run every 12 hours' },
  { key: 'daily', label: 'Daily (Midnight)', cron_expr: '0 0 * * *', description: 'Run daily at midnight' },
  { key: 'daily_9am', label: 'Daily (9 AM)', cron_expr: '0 9 * * *', description: 'Run daily at 9:00 AM' },
  { key: 'daily_noon', label: 'Daily (Noon)', cron_expr: '0 12 * * *', description: 'Run daily at noon' },
  { key: 'weekly_sunday', label: 'Weekly (Sunday)', cron_expr: '0 0 * * 0', description: 'Run every Sunday at midnight' },
  { key: 'weekly_monday', label: 'Weekly (Monday)', cron_expr: '0 0 * * 1', description: 'Run every Monday at midnight' },
  { key: 'monthly', label: 'Monthly (1st)', cron_expr: '0 0 1 * *', description: 'Run on the 1st of every month' },
  { key: 'quarterly', label: 'Quarterly', cron_expr: '0 0 1 1,4,7,10 *', description: 'Run on the 1st of Jan, Apr, Jul, Oct' },
];

// Type guards for schedules
export const isScheduleStatus = (status: string): status is ScheduleExecution['status'] => {
  return SCHEDULE_STATUSES.includes(status as any);
};

export const isTargetSelectionMode = (mode: string): mode is ScanSchedule['target_selection_mode'] => {
  return TARGET_SELECTION_MODES.includes(mode as any);
};

export const isTriggeredBy = (triggeredBy: string): triggeredBy is ScheduleExecution['triggered_by'] => {
  return TRIGGERED_BY_OPTIONS.includes(triggeredBy as any);
};

// Default schedule values
export const DEFAULT_SCHEDULE: Partial<ScanSchedule> = {
  enabled: true,
  cron_expr: '0 0 * * *', // Daily at midnight
  target_selection_mode: 'fixed',
  max_runs: 0, // 0 = unlimited
  enable_comparison: true,
  compare_with_last_n: 1,
  retain_historical_scans: 10,
  notify_on_completion: false,
  notify_on_failure: true,
  notify_on_new_vulns: true,
};
